import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { Outlet } from 'react-router-dom';

// Reads a non-HttpOnly cookie value (the antiforgery request token the BFF sets on
// /api/v1/auth/user). Used to populate the hidden field of the logout form.
function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

// Mirrors the contract of `@magni/webapp-bff-auth` (BffAuthProvider, useBffAuth,
// ProtectedRoute, getApiClient). Inlined here to avoid pulling a private npm registry —
// the package isn't published anywhere a clean `npm install` can reach.

export interface BffUser {
  userId: number;
  name: string;
  email: string;
  expiresAt: string;
  isAuthenticated: true;
  roles: string[];
}

interface AuthState {
  user: BffUser | null;
  isAuthenticated: boolean;
  isAuthorized: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: (returnUrl?: string) => void;
  logout: (returnUrl?: string) => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const REQUIRED_ROLE = 'MagniAdmin';

// Single-flight: a burst of components mounting at once all share one fetch.
let inFlight: Promise<BffUser | null> | null = null;
async function fetchCurrentUser(): Promise<BffUser | null> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const r = await fetch('/api/v1/auth/user', { credentials: 'include' });
      if (!r.ok) return null;
      const body = await r.json();
      if (!body?.isAuthenticated) return null;
      return body as BffUser;
    } catch {
      return null;
    } finally {
      // Allow the next call to refetch — but not before the current microtask drains.
      setTimeout(() => { inFlight = null; }, 0);
    }
  })();
  return inFlight;
}

export function BffAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isAuthorized: false,
    isLoading: true,
    error: null,
  });

  const checkAuth = useCallback(async () => {
    setState((s) => ({ ...s, isLoading: true }));
    const user = await fetchCurrentUser();
    setState({
      user,
      isAuthenticated: user != null,
      isAuthorized: user?.roles?.includes(REQUIRED_ROLE) ?? false,
      isLoading: false,
      error: null,
    });
  }, []);

  useEffect(() => { void checkAuth(); }, [checkAuth]);

  const login = useCallback((returnUrl?: string) => {
    const target = returnUrl ?? window.location.pathname + window.location.search;
    window.location.assign(`/api/v1/auth/login?returnUrl=${encodeURIComponent(target)}`);
  }, []);

  const logout = useCallback((returnUrl?: string) => {
    // Logout is a state-changing POST that then redirects through the auth server's
    // end-session endpoint — so it must be a top-level form navigation (an XHR can't follow
    // the cross-origin redirect). Submit a hidden, antiforgery-tokened form.
    const target = returnUrl ?? '/';
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `/api/v1/auth/logout?returnUrl=${encodeURIComponent(target)}`;
    const token = readCookie('XSRF-TOKEN');
    if (token) {
      const field = document.createElement('input');
      field.type = 'hidden';
      field.name = '__RequestVerificationToken';
      field.value = token;
      form.appendChild(field);
    }
    document.body.appendChild(form);
    form.submit();
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    ...state, login, logout, checkAuth,
  }), [state, login, logout, checkAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useBffAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useBffAuth must be used inside <BffAuthProvider>');
  return ctx;
}

/**
 * Protects routes — bounces unauthenticated users to /api/v1/auth/login (which triggers
 * the OIDC challenge on the BFF). Authenticated-but-unauthorized users see the
 * AccessDenied component if one is provided; otherwise a default message.
 */
export function ProtectedRoute({
  loadingComponent,
  accessDenied,
}: {
  loadingComponent?: ReactNode;
  accessDenied?: ReactNode;
}) {
  const { isAuthenticated, isAuthorized, isLoading, login } = useBffAuth();
  const triggered = useRef(false);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !triggered.current) {
      triggered.current = true;
      login();
    }
  }, [isLoading, isAuthenticated, login]);

  if (isLoading) return <>{loadingComponent ?? <DefaultLoading />}</>;
  if (!isAuthenticated) return <DefaultRedirecting />;
  if (!isAuthorized) return <>{accessDenied ?? <DefaultAccessDenied />}</>;
  return <Outlet />;
}

function DefaultLoading() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background-light">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-magni-red mx-auto mb-3" />
        <p className="text-gray-500">A verificar a autenticação…</p>
      </div>
    </div>
  );
}

function DefaultRedirecting() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background-light">
      <p className="text-gray-500">A redirecionar para o início de sessão…</p>
    </div>
  );
}

function DefaultAccessDenied() {
  const { logout } = useBffAuth();
  return (
    <div className="flex items-center justify-center min-h-screen bg-background-light">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center border border-gray-200">
        <span className="material-symbols-outlined text-5xl text-magni-red mb-4">block</span>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Acesso recusado</h2>
        <p className="text-gray-600 mb-6">
          Não tem o papel <strong>MagniAdmin</strong> necessário para aceder ao MagniUsage.
        </p>
        <button
          onClick={() => logout()}
          className="px-4 py-2 bg-magni-blue text-white rounded-md hover:bg-magni-blue-700 transition-colors duration-200"
        >Terminar sessão</button>
      </div>
    </div>
  );
}
